/**
 * Picture-in-Picture Video Player
 * A floating, draggable mini video player for product pages
 * 
 * Features:
 * - Auto-appears after configurable delay
 * - Muted by default with unmute toggle
 * - Loops video 3 times then auto-closes
 * - Draggable anywhere on screen
 * - Expand button scrolls to main gallery video
 * - Mobile optimized with touch support
 */

class PipVideoPlayer extends HTMLElement {
  constructor() {
    super();

    // Configuration
    this.delay = parseInt(this.dataset.delay) || 2500;
    this.maxLoops = parseInt(this.dataset.maxLoops) || 3;
    this.mediaType = this.dataset.mediaType;
    this.mediaIndex = parseInt(this.dataset.mediaIndex) || 0;
    this.externalHost = this.dataset.host;
    this.aspectRatio = parseFloat(this.dataset.aspectRatio) || 1.7777;

    // Dynamic Sizing Logic
    // Landscape (AR >= 1): Fixed Width 220px
    // Vertical (AR < 1): Fixed Height 220px -> Width = 220 * AR
    // This keeps the "visual size" consistent without vertical videos becoming huge
    const BASE_WIDTH = 220;
    if (this.aspectRatio < 1) {
      // Vertical
      this.style.width = `${Math.round(BASE_WIDTH * this.aspectRatio)}px`; // e.g. 220 * 9/16 = ~124px
      // Height will be implicitly determined by aspect-ratio CSS or content
    } else {
      // Landscape
      this.style.width = `${BASE_WIDTH}px`;
    }

    // Set aspect ratio CSS variable for container
    this.style.setProperty('--pip-aspect-ratio', this.aspectRatio);

    // State
    this.loopCount = 0;
    this.isMuted = true;
    this.isDragging = false;
    this.isVisible = false;

    // Drag state
    this.dragStartX = 0;
    this.dragStartY = 0;
    this.initialLeft = 0;
    this.initialTop = 0;

    // Bound methods for event listeners
    this.handleMouseDown = this.handleMouseDown.bind(this);
    this.handleMouseMove = this.handleMouseMove.bind(this);
    this.handleMouseUp = this.handleMouseUp.bind(this);
    this.handleTouchStart = this.handleTouchStart.bind(this);
    this.handleTouchMove = this.handleTouchMove.bind(this);
    this.handleTouchEnd = this.handleTouchEnd.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.handleVideoEnded = this.handleVideoEnded.bind(this);
  }

  connectedCallback() {
    // Get elements
    this.closeBtn = this.querySelector('.pip-video__close');
    this.expandBtn = this.querySelector('.pip-video__expand');
    this.unmuteBtn = this.querySelector('.pip-video__unmute');
    this.video = this.querySelector('.pip-video__video');
    this.iframe = this.querySelector('.pip-video__iframe');
    this.loopCurrentEl = this.querySelector('.pip-video__loop-current');
    this.loopTotalEl = this.querySelector('.pip-video__loop-total');

    // Set total loops display
    if (this.loopTotalEl) {
      this.loopTotalEl.textContent = this.maxLoops;
    }

    // Setup event listeners
    this.setupEventListeners();

    // Schedule appearance
    this.scheduleShow();
  }

  disconnectedCallback() {
    this.removeEventListeners();
    this.cleanupExternalVideoLoop();
    if (this.showTimeout) {
      clearTimeout(this.showTimeout);
    }
  }

  setupEventListeners() {
    // Close button
    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.hide();
      });
    }

    // Expand button
    if (this.expandBtn) {
      this.expandBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.expandToMainVideo();
      });
    }

    // Unmute button
    if (this.unmuteBtn) {
      this.unmuteBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.toggleMute();
      });
    }

    // Video ended event for loop counting (Shopify hosted videos)
    if (this.video) {
      this.video.addEventListener('ended', this.handleVideoEnded);
      // Also track timeupdate for more reliable loop detection
      this.video.addEventListener('timeupdate', () => {
        // For looping videos, detect when video restarts
        if (this.video.loop && this.video.currentTime < 0.5 && this.lastTime > this.video.duration - 1) {
          this.handleLoopComplete();
        }
        this.lastTime = this.video.currentTime;
      });
    }

    // For external videos, use time-based loop detection
    if (this.iframe && this.mediaType === 'external_video') {
      this.startExternalVideoLoopDetection();
    }

    // Drag events - mouse
    this.addEventListener('mousedown', this.handleMouseDown);

    // Drag events - touch
    this.addEventListener('touchstart', this.handleTouchStart, { passive: false });

    // Keyboard events
    document.addEventListener('keydown', this.handleKeyDown);
  }

  removeEventListeners() {
    document.removeEventListener('mousemove', this.handleMouseMove);
    document.removeEventListener('mouseup', this.handleMouseUp);
    document.removeEventListener('touchmove', this.handleTouchMove);
    document.removeEventListener('touchend', this.handleTouchEnd);
    document.removeEventListener('keydown', this.handleKeyDown);

    if (this.video) {
      this.video.removeEventListener('ended', this.handleVideoEnded);
    }
  }

  scheduleShow() {
    this.showTimeout = setTimeout(() => {
      this.show();
    }, this.delay);
  }

  show() {
    if (this.isVisible) return;

    this.isVisible = true;
    this.hidden = false;
    this.classList.add('pip-video--entering');

    // Start video playback
    this.playVideo();

    // Remove entering class after animation
    setTimeout(() => {
      this.classList.remove('pip-video--entering');
    }, 400);
  }

  hide() {
    if (!this.isVisible) return;

    this.classList.add('pip-video--leaving');

    // Pause video
    this.pauseVideo();

    // Clean up external video loop detection
    this.cleanupExternalVideoLoop();

    // Remove after animation
    setTimeout(() => {
      this.isVisible = false;
      this.hidden = true;
      this.classList.remove('pip-video--leaving');
    }, 300);
  }

  playVideo() {
    if (this.video) {
      this.video.play().catch(() => {
        // Autoplay might be blocked, that's okay
        console.log('PiP video autoplay blocked by browser');
      });
    }
  }

  pauseVideo() {
    if (this.video) {
      this.video.pause();
    }
    // For YouTube/Vimeo, we'd need postMessage but since we hide the whole element, it stops anyway
  }

  toggleMute() {
    this.isMuted = !this.isMuted;

    if (this.video) {
      this.video.muted = this.isMuted;
    }

    // For YouTube iframe
    if (this.iframe && this.externalHost === 'youtube') {
      const command = this.isMuted ? 'mute' : 'unMute';
      this.iframe.contentWindow.postMessage(
        JSON.stringify({ event: 'command', func: command, args: '' }),
        '*'
      );
    }

    // For Vimeo iframe
    if (this.iframe && this.externalHost === 'vimeo') {
      this.iframe.contentWindow.postMessage(
        JSON.stringify({ method: 'setVolume', value: this.isMuted ? 0 : 1 }),
        '*'
      );
    }

    // Update visual state
    this.classList.toggle('pip-video--unmuted', !this.isMuted);
  }

  handleVideoEnded() {
    // For non-looping videos
    this.handleLoopComplete();
  }

  handleLoopComplete() {
    this.loopCount++;

    // Update loop counter display
    if (this.loopCurrentEl) {
      this.loopCurrentEl.textContent = Math.min(this.loopCount + 1, this.maxLoops);
    }

    // Check if we've reached max loops
    if (this.loopCount >= this.maxLoops) {
      this.hide();
    }
  }

  startExternalVideoLoopDetection() {
    // For external videos, we estimate based on typical video length
    // Since we can't reliably get duration from iframe, we'll use a fallback
    // Most product videos are 15-60 seconds, we'll estimate 30 seconds per loop
    const estimatedLoopDuration = 30000; // 30 seconds

    this.externalLoopInterval = setInterval(() => {
      this.handleLoopComplete();

      if (this.loopCount >= this.maxLoops) {
        clearInterval(this.externalLoopInterval);
      }
    }, estimatedLoopDuration);
  }

  // Performance: Clean up interval on hide
  cleanupExternalVideoLoop() {
    if (this.externalLoopInterval) {
      clearInterval(this.externalLoopInterval);
      this.externalLoopInterval = null;
    }
  }

  expandToMainVideo() {
    // Find the media gallery
    const mediaGallery = document.querySelector('media-gallery');
    const sectionId = mediaGallery?.id?.replace('MediaGallery-', '') || '';

    if (mediaGallery) {
      // Try multiple selectors to find the correct video slide
      // 1. Try with section-prefixed media ID (Shopify's format)
      let videoSlide = mediaGallery.querySelector(
        `[data-media-id="${sectionId}-${this.dataset.mediaId}"]`
      );

      // 2. Try with just the media ID
      if (!videoSlide) {
        videoSlide = mediaGallery.querySelector(
          `[data-media-id="${this.dataset.mediaId}"]`
        );
      }

      // 3. Try finding by slide ID
      if (!videoSlide) {
        videoSlide = document.getElementById(`Slide-${sectionId}-${this.dataset.mediaId}`);
      }

      // 4. Fallback: find any slide containing video/deferred-media with video
      if (!videoSlide) {
        const allSlides = mediaGallery.querySelectorAll('.product__media-item');
        for (const slide of allSlides) {
          const hasVideo = slide.querySelector('video, deferred-media[data-media-type="video"], deferred-media[data-media-type="external_video"]');
          if (hasVideo) {
            videoSlide = slide;
            break;
          }
        }
      }

      if (videoSlide) {
        // Scroll to the video with offset for better visibility
        videoSlide.scrollIntoView({ behavior: 'smooth', block: 'center' });

        // For mobile slider, try to activate this slide
        const slider = mediaGallery.querySelector('.slider');
        if (slider && typeof mediaGallery.setActiveMedia === 'function') {
          const mediaId = videoSlide.dataset.mediaId;
          if (mediaId) {
            mediaGallery.setActiveMedia(mediaId, true);
          }
        }

        // Try to click the deferred media button to load/play the video
        setTimeout(() => {
          const deferredMedia = videoSlide.querySelector('deferred-media');
          if (deferredMedia) {
            const playButton = deferredMedia.querySelector('.deferred-media__poster-button');
            if (playButton) {
              playButton.click();
            } else {
              // Try alternative: directly load content if method exists
              if (typeof deferredMedia.loadContent === 'function') {
                deferredMedia.loadContent();
              }
            }
          }
        }, 600);
      }
    }

    // Hide the PiP after expanding
    this.hide();
  }

  // =====================
  // Drag functionality
  // =====================

  handleMouseDown(e) {
    // Don't drag if clicking on buttons
    if (e.target.closest('button')) return;

    this.startDrag(e.clientX, e.clientY);

    document.addEventListener('mousemove', this.handleMouseMove);
    document.addEventListener('mouseup', this.handleMouseUp);
  }

  handleMouseMove(e) {
    if (!this.isDragging) return;
    this.drag(e.clientX, e.clientY);
  }

  handleMouseUp() {
    this.endDrag();
    document.removeEventListener('mousemove', this.handleMouseMove);
    document.removeEventListener('mouseup', this.handleMouseUp);
  }

  handleTouchStart(e) {
    // Don't drag if touching buttons
    if (e.target.closest('button')) return;

    const touch = e.touches[0];
    this.startDrag(touch.clientX, touch.clientY);

    document.addEventListener('touchmove', this.handleTouchMove, { passive: false });
    document.addEventListener('touchend', this.handleTouchEnd);
  }

  handleTouchMove(e) {
    if (!this.isDragging) return;
    e.preventDefault(); // Prevent scrolling while dragging

    const touch = e.touches[0];
    this.drag(touch.clientX, touch.clientY);
  }

  handleTouchEnd() {
    this.endDrag();
    document.removeEventListener('touchmove', this.handleTouchMove);
    document.removeEventListener('touchend', this.handleTouchEnd);
  }

  startDrag(clientX, clientY) {
    this.isDragging = true;
    this.classList.add('pip-video--dragging');

    this.dragStartX = clientX;
    this.dragStartY = clientY;

    const rect = this.getBoundingClientRect();
    this.initialLeft = rect.left;
    this.initialTop = rect.top;

    // Switch from bottom/left positioning to top/left for dragging
    this.style.bottom = 'auto';
    this.style.right = 'auto';
    this.style.left = `${rect.left}px`;
    this.style.top = `${rect.top}px`;
  }

  drag(clientX, clientY) {
    const deltaX = clientX - this.dragStartX;
    const deltaY = clientY - this.dragStartY;

    let newLeft = this.initialLeft + deltaX;
    let newTop = this.initialTop + deltaY;

    // Keep within viewport bounds
    const rect = this.getBoundingClientRect();
    const maxLeft = window.innerWidth - rect.width;
    const maxTop = window.innerHeight - rect.height;

    newLeft = Math.max(0, Math.min(newLeft, maxLeft));
    newTop = Math.max(0, Math.min(newTop, maxTop));

    this.style.left = `${newLeft}px`;
    this.style.top = `${newTop}px`;
  }

  endDrag() {
    this.isDragging = false;
    this.classList.remove('pip-video--dragging');
  }

  // =====================
  // Keyboard handling
  // =====================

  handleKeyDown(e) {
    if (!this.isVisible) return;

    // Close on Escape
    if (e.key === 'Escape') {
      this.hide();
    }
  }
}

// Register custom element
if (!customElements.get('pip-video-player')) {
  customElements.define('pip-video-player', PipVideoPlayer);
}
